import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { findAum, findStrategy, processData, signalsData } from "../../../component/Methods";
import MonitorScreenCalculation from "./MonitorScreenCalculation";
import { resolveFundAbbreviation } from "./portfolioFundUtils";
import { setCalculatedMonitorScreen } from "../../../Redux/Reducers/marketDataSlice";
import { updateLoading, updateLoadingMsg, selectPositionCalculationMode, selectNavDenominatorMode } from "../../../Redux/Reducers/uiSlice";
import {
  getMonitorScreenTradeParams,
  isCalculatedNavMode,
} from "../../../utils/positionCalculationMode";

export const usePortfolioCalculations = ({
  funds,
  lots,
  accountingData,
  strategies,
  livePrices,
  trades,
  markets,
  isStrategist,
  managers,
  signalsPerformance,
}) => {
  const dispatch = useDispatch();
  const positionCalculationMode = useSelector(selectPositionCalculationMode);
  const navDenominatorMode = useSelector(selectNavDenominatorMode);

  // Memoize derived values
  const fund = useMemo(() => accountingData[0]?.funds, [accountingData]);

  // Filter data calculation
  const filterData = useCallback(
    (fundVal, StrategyVal, StrategeyTypename) => {
      let processedFundVal = fundVal;
      let processedStrategyVal = StrategyVal;

      if (fundVal === "ALL" || StrategyVal === "ALL") {
        processedFundVal = undefined;
        if (!isStrategist) processedStrategyVal = undefined;
      }

      const resolvedAbbrev = resolveFundAbbreviation(funds, processedFundVal);
      const fundObject = funds?.find((obj) => obj.abbreviation === resolvedAbbrev);
      if (fundObject) processedFundVal = fundObject.fundId;

      const { filled, trades: tradesForCalc } = getMonitorScreenTradeParams(
        positionCalculationMode,
        trades
      );

      const filteredData = MonitorScreenCalculation({
        selectedFund: processedFundVal,
        selectedStrategy: processedStrategyVal,
        selectedSide: StrategeyTypename,
        lots,
        accountingData,
        funds,
        strategies,
        marketData: livePrices,
        trades: tradesForCalc,
        filled,
        isStrategist,
        markets,
        useCalculatedNav: isCalculatedNavMode(navDenominatorMode),
      });
            if (filteredData) {
        dispatch(setCalculatedMonitorScreen(filteredData));
      }

      return filteredData;
    },
    [funds, isStrategist, lots, accountingData, strategies, livePrices, trades, markets, dispatch, positionCalculationMode, navDenominatorMode]
  );

  // Signal calculations
  const calculateSignals = useCallback(
    (fnd, stra, typ) => {
      if (!fnd || !stra) {
        return { Long: 0, Short: 0 };
      }

      if (stra === undefined || stra === "ALL" || stra === "") {
        return { Long: 0, Short: 0 };
      }

      let matchingStrategies;
      if (typ === "Both") {
        matchingStrategies = strategies?.find((strategy) => strategy.name === stra);
      } else if (typ === "Long" || typ === "Short") {
        matchingStrategies = strategies?.find((strategy) =>
          strategy.name.includes(`${stra}_${typ}`)
        );
      }

      if (matchingStrategies) {
        const signalsCalc = signalsData(signalsPerformance, strategies, matchingStrategies.strategyId);

        const positiveValues = signalsCalc
          .filter((signal) => signal.value >= 0)
          .reduce((sum, signal) => sum + signal.PercentChangeDay * signal.value, 0);

        const negativeValues = signalsCalc
          .filter((signal) => signal.value < 0)
          .reduce((sum, signal) => sum + signal.PercentChangeDay * signal.value, 0);

        return {
          Long: positiveValues,
          Short: negativeValues,
        };
      }

      return { Long: 0, Short: 0 };
    },
    [strategies, signalsPerformance]
  );

  // Main calculation function
  const calculatePositions = useCallback(
    (fnd, stra, typ, setFilteredValues, setTotals) => {
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Calculating Positions"));

      // Clear caches periodically to prevent memory leaks
      if (Math.random() < 0.01) {
        if (typeof window !== "undefined") {
          if (window.priceCache) window.priceCache.clear();
          if (window.deltaCache) window.deltaCache.clear();
          if (window.strategyCache) window.strategyCache.clear();
          if (window.fundCache) window.fundCache.clear();
        }
      }

      // Use setTimeout to prevent blocking the UI
      setTimeout(() => {
        const monitorScreenValuesObj = filterData(fnd, stra, typ);
        const { Long, Short } = calculateSignals(fnd, stra, typ);

        if (!monitorScreenValuesObj) {
          setFilteredValues([]);
          setTotals([]);
          dispatch(updateLoading(false));
          dispatch(updateLoadingMsg(""));
          return;
        }

   
        const resolveRowAum = (obj) => {
          if (isStrategist) {
            const byId = strategies?.find(
              (s) => s.strategyId === obj?.strategyName
            );
            if (byId?.aum != null && !isNaN(Number(byId.aum))) {
              return Number(byId.aum);
            }
            if (stra && stra !== "ALL" && stra !== "") {
              const matching = strategies?.filter(
                (strategy) =>
                  strategy?.name === stra ||
                  strategy?.name?.startsWith(`${stra}_`)
              );
              if (matching?.length > 0 && matching[0]?.aum != null) {
                return Number(matching[0].aum);
              }
            }
            const totalStrategyAum = strategies?.reduce(
              (sum, s) => sum + (parseFloat(s.aum) || 0),
              0
            );
            return totalStrategyAum || undefined;
          }

          if (obj.fund === "All") {
            return accountingData[0]?.funds.reduce((total, f) => total + f.aum, 0);
          }
          if (obj?.strategyName !== "All") {
            const selectedFund = accountingData[0]?.funds.find(
              (fund) => fund.fund === obj.fund
            );
            if (selectedFund) {
              return selectedFund?.strategyAUMs?.[obj?.strategyName];
            }
          }
          return findAum(obj.fund, accountingData[0]?.funds);
        };

        let aum;
        const result = monitorScreenValuesObj.reduce((acc, obj) => {
          // Trade-injected rows carry fund "ALL"/no strategy and don't resolve to an
          // AUM — keep the last valid AUM so they don't clobber it with undefined.
          const resolvedAum = resolveRowAum(obj);
          if (resolvedAum != null && !isNaN(resolvedAum)) {
            aum = resolvedAum;
          }

          const today = Number(obj.deltaAdjustedShareCount);
          const overnight = Number(
            obj.deltaAdjustedShareCountWithoutTrades ?? obj.deltaAdjustedShareCount
          );
          const pl = parseFloat(obj.pL);

          if (today > 0) {
            acc.positiveAUMTotal += parseFloat(obj.size) || 0;
            acc.positivePLTotal += pl || 0;
          } else if (today < 0) {
            acc.negativeAUMTotal += parseFloat(obj.size) || 0;
            acc.negativePLTotal += pl || 0;
          } else if (overnight > 0) {
            // Closed today: keep P(L) on yesterday's side, skip %AUM.
            acc.positivePLTotal += pl || 0;
          } else if (overnight < 0) {
            acc.negativePLTotal += pl || 0;
          }
          return acc;
        }, {
          positiveAUMTotal: 0,
          negativeAUMTotal: 0,
          positivePLTotal: 0,
          negativePLTotal: 0,
        });

        const totalPL = result.positivePLTotal + result.negativePLTotal;
        const totalAUM = result.positiveAUMTotal - result.negativeAUMTotal;
        const hasValidAum = aum != null && !isNaN(aum) && aum !== 0;

        const newArray = [
          {
            name: "LONG",
            totalsAUM: result.positiveAUMTotal,
            totalsPl: result.positivePLTotal,
            totalSignals: Long,
            totalsPcm:
              !hasValidAum ||
              !result.positiveAUMTotal ||
              isNaN(result.positivePLTotal) ||
              result.positivePLTotal === 0
                ? "-"
                : (result.positivePLTotal / ((result.positiveAUMTotal * aum) / 100)) * 100,
          },
          {
            name: "SHORT",
            totalsAUM: -result.negativeAUMTotal,
            totalsPl: result.negativePLTotal,
            totalsPcm:
              !hasValidAum ||
              !result.negativeAUMTotal ||
              isNaN(result.negativePLTotal) ||
              result.negativePLTotal === 0
                ? "-"
                : (result.negativePLTotal / ((result.negativeAUMTotal * aum) / 100)) * 100 ,
            totalSignals: Short,
          },
          {
            name: "TOTALS",
            totalsAUM: isNaN(totalAUM) ? "-" : totalAUM,
            totalsPl: isNaN(totalPL) ? "-" : totalPL,
            totalsPcm: !hasValidAum || isNaN(totalPL) ? "-" : (totalPL / aum) * 100,
            totalSignals: Short + Long,
            aumDollars: aum
          },
        ];

        setFilteredValues(monitorScreenValuesObj);
        setTotals(newArray);

        dispatch(updateLoading(false));
        dispatch(updateLoadingMsg(""));
      }, 0);
    },
    [
      filterData,
      calculateSignals,
      accountingData,
      dispatch,
      isStrategist,
      strategies,
    ]
  );

  return {
    fund,
    filterData,
    calculateSignals,
    calculatePositions,
  };
};
